import React, {
  useCallback,
  useRef,
  useState,
  KeyboardEvent,
  useEffect,
} from "react";
import { theme } from "../../../../constants/StyledComponents";
import {
  StyledFieldContainer,
  StyledInputContainer,
  StyledIconContainer,
  StyledCloseContainer,
  StyledLabel,
} from "../../Input/Input.styled";
import { IconManager } from "../../IconManager/IconManager";
import { ReactComponent as CloseIcon } from "../../../../icons/close.svg";
import { FontWeightEnum, IconSizeEnum } from "../../../../types/AppTypes";
import { BodyTyp } from "../../../Typography/Typography";
import { InputSizes, InputBorderEnum } from "../../Input/Input";
import { CalendarMode } from "../CalendarInput";
import {
  StyledSegmentsRow,
  StyledSegment,
  StyledSeparator,
} from "./DateInput.styled";

type Segment = { day: string; month: string; year: string };

interface DateInputProps {
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  label?: string;
  hasError?: boolean;
  disabled?: boolean;
  pickerMode?: CalendarMode;
  endIcon?: React.ReactNode;
  inputSize?: InputSizes;
  borderVariant?: InputBorderEnum;
}

const dateToSegments = (
  date: Date | null | undefined,
  mode: CalendarMode,
): Segment => {
  if (!date || isNaN(date.getTime())) return { day: "", month: "", year: "" };
  return {
    day:
      mode === CalendarMode.DAYS ? String(date.getDate()).padStart(2, "0") : "",
    month:
      mode !== CalendarMode.YEARS
        ? String(date.getMonth() + 1).padStart(2, "0")
        : "",
    year: String(date.getFullYear()),
  };
};

const segmentsToDate = (seg: Segment, mode: CalendarMode): Date | null => {
  if (!seg.year) return null;
  const year = parseInt(seg.year);
  if (isNaN(year)) return null;

  if (mode === CalendarMode.YEARS) {
    return seg.year.length === 4 ? new Date(year, 0, 1) : null;
  }

  const month = parseInt(seg.month);
  if (!seg.month || seg.month.length < 2 || isNaN(month)) return null;
  if (month < 1 || month > 12) return null;

  if (mode === CalendarMode.MONTHS) {
    return seg.year.length === 4 ? new Date(year, month - 1, 1) : null;
  }

  const day = parseInt(seg.day);
  if (!seg.day || seg.day.length < 2 || isNaN(day)) return null;
  if (day < 1 || day > 31) return null;

  if (seg.year.length < 4) return null;

  const d = new Date(year, month - 1, day);
  if (d.getDate() !== day || d.getMonth() !== month - 1) return null;
  return d;
};

export const DateInput = ({
  value,
  onChange,
  label,
  hasError = false,
  disabled,
  pickerMode = CalendarMode.DAYS,
  endIcon,
  inputSize = InputSizes.REGULAR,
  borderVariant = InputBorderEnum.DEFAULT,
}: DateInputProps) => {
  const [segments, setSegments] = useState<Segment>(() =>
    dateToSegments(value, pickerMode),
  );
  const [active, setActive] = useState<keyof Segment | null>(null);
  const shouldReset = useRef(false);
  const dayRef = useRef<HTMLSpanElement>(null);
  const monthRef = useRef<HTMLSpanElement>(null);
  const yearRef = useRef<HTMLSpanElement>(null);

  const segmentRefs = { day: dayRef, month: monthRef, year: yearRef };

  const focusSegment = (field: keyof Segment) => {
    segmentRefs[field].current?.focus();
  };
  useEffect(() => {
    if (value && isNaN(value.getTime())) return;
    setSegments(dateToSegments(value, pickerMode));
  }, [value, pickerMode]);

  const update = useCallback(
    (next: Segment) => {
      setSegments(next);
      const date = segmentsToDate(next, pickerMode);
      const allEmpty = !next.day && !next.month && !next.year;
      if (allEmpty) onChange?.(null);
      else onChange?.(date ?? new Date("invalid"));
    },
    [onChange, pickerMode],
  );

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>, field: keyof Segment) => {
      if (disabled) return;
      const isYear = field === "year";
      const len = isYear ? 4 : 2;

      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        const current = shouldReset.current ? "" : segments[field];
        shouldReset.current = false;
        const raw = (current + e.key).slice(-len);
        const next = { ...segments, [field]: raw };

        update(next);
        if (field === "year" && next.year.length === 4) {
          shouldReset.current = true;
        }
        if (next[field].length === len) {
          const nextField =
            field === "day" ? "month" : field === "month" ? "year" : null;
          if (nextField) {
            const nf = nextField as keyof Segment;
            shouldReset.current = next[nf].length === (nf === "year" ? 4 : 2);
            focusSegment(nf);
          }
        }
      } else if (e.key === "Backspace") {
        e.preventDefault();
        shouldReset.current = false;
        if (segments[field] === "") {
          const prevField =
            field === "year"
              ? "month"
              : field === "month" && pickerMode === CalendarMode.DAYS
                ? "day"
                : null;
          if (prevField) focusSegment(prevField as keyof Segment);
        } else {
          update({ ...segments, [field]: segments[field].slice(0, -1) });
        }
      } else if (e.key === "ArrowRight" || (e.key === "Tab" && !e.shiftKey)) {
        const nextField =
          field === "day" ? "month" : field === "month" ? "year" : null;
        if (nextField) {
          e.preventDefault();
          focusSegment(nextField as keyof Segment);
        }
      } else if (e.key === "ArrowLeft" || (e.key === "Tab" && e.shiftKey)) {
        const prevField =
          field === "year" ? "month" : field === "month" ? "day" : null;
        if (prevField) {
          e.preventDefault();
          focusSegment(prevField as keyof Segment);
        }
      }
    },
    [segments, update, disabled],
  );

  const clear = () => {
    setSegments({ day: "", month: "", year: "" });
    onChange?.(null);
  };

  const hasValue = !!(segments.day || segments.month || segments.year);
  const isActive = active !== null;

  return (
    <StyledFieldContainer $hasError={!!hasError}>
      <StyledInputContainer
        disabled={disabled}
        $hasError={!!hasError}
        $inputSize={inputSize}
        $borderVariant={borderVariant}
        style={isActive ? { borderColor: theme.colors.purple } : undefined}
      >
        <StyledSegmentsRow
          $hasEndIcon={!!endIcon}
          $hasLabel={!!label}
          $disabled={disabled}
        >
          {pickerMode === CalendarMode.DAYS && (
            <>
              <StyledSegment
                tabIndex={disabled ? -1 : 0}
                $active={active === "day"}
                $empty={!segments.day}
                onFocus={() => {
                  setActive("day");
                  shouldReset.current = segments.day.length === 2;
                }}
                onBlur={() => setActive(null)}
                onKeyDown={(e) => handleKey(e, "day")}
                ref={segmentRefs.day}
              >
                {segments.day || "GG"}
              </StyledSegment>
              <StyledSeparator>/</StyledSeparator>
            </>
          )}
          {pickerMode !== CalendarMode.YEARS && (
            <>
              <StyledSegment
                tabIndex={disabled ? -1 : 0}
                $active={active === "month"}
                $empty={!segments.month}
                onFocus={() => {
                  setActive("month");
                  shouldReset.current = segments.month.length === 2;
                }}
                onBlur={() => setActive(null)}
                onKeyDown={(e) => handleKey(e, "month")}
                ref={segmentRefs.month}
              >
                {segments.month || "MM"}
              </StyledSegment>
              <StyledSeparator>/</StyledSeparator>
            </>
          )}
          <StyledSegment
            tabIndex={disabled ? -1 : 0}
            $active={active === "year"}
            $empty={!segments.year}
            onFocus={() => {
              setActive("year");
              shouldReset.current = segments.year.length === 4;
            }}
            onBlur={() => setActive(null)}
            onKeyDown={(e) => handleKey(e, "year")}
            ref={segmentRefs.year}
          >
            {segments.year || "AAAA"}
          </StyledSegment>
        </StyledSegmentsRow>
        <StyledLabel
          $hasError={!!hasError}
          $hasEndIcon={!!endIcon}
          $allowClear={true}
          $inputSize={inputSize}
          $isActive={hasValue || isActive}
          $hasPlaceholder={true}
        >
          <BodyTyp
            variant="2"
            fontWeight={FontWeightEnum.REGULAR}
            color={hasError ? theme.colors.red_1 : theme.colors.grey_2}
          >
            {label}
          </BodyTyp>
        </StyledLabel>
        <StyledIconContainer>
          {!disabled && (
            <StyledCloseContainer $visible={hasValue}>
              <IconManager size={IconSizeEnum.SMALL} onClick={clear}>
                <CloseIcon />
              </IconManager>
            </StyledCloseContainer>
          )}
          {endIcon}
        </StyledIconContainer>
      </StyledInputContainer>
    </StyledFieldContainer>
  );
};
